import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { useMemo, useState } from 'react'
import { Mic } from 'lucide-react'
import type { FlashcardGameTemplateProps } from '../template-types'
import { compareFlashcardAnswer } from '../session-factory'
import { playLearningFeedbackAudio } from '@features/learning/shared/learning-feedback-audio'
import { Field, FieldLabel, LmsButton, LmsInput } from '@shared/ui'
import {
  LearningTemplateContinueActions,
  LearningTemplateExerciseFrame,
  LearningTemplatePromptMedia,
} from '@features/learning/shared/templates'
import '../flashcard-game.css'

interface BrowserSpeechRecognition {
  lang: string
  onerror: (() => void) | null
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null
  start: () => void
  stop: () => void
}

interface BrowserSpeechRecognitionConstructor {
  new (): BrowserSpeechRecognition
}

function getSpeechRecognitionConstructor() {
  if (typeof window === 'undefined') {
    return null
  }

  const browserWindow = window as typeof window & {
    readonly SpeechRecognition?: BrowserSpeechRecognitionConstructor
    readonly webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor
  }

  return (
    browserWindow.SpeechRecognition ?? browserWindow.webkitSpeechRecognition ?? null
  )
}

export function Template5({
  onContinue,
  prompt,
}: FlashcardGameTemplateProps) {
  const ui = useLearningUiTranslation()
  const [value, setValue] = useState('')
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null)
  const [isListening, setIsListening] = useState(false)
  const SpeechRecognition = useMemo(() => getSpeechRecognitionConstructor(), [])

  const handleCheckAnswer = () => {
    const nextResult = compareFlashcardAnswer({
      actual: value,
      expected: prompt.answerText ?? '',
    })
      ? 'correct'
      : 'incorrect'

    setResult(nextResult)
    playLearningFeedbackAudio(nextResult)
  }

  const handleSpeechInput = () => {
    if (!SpeechRecognition) {
      return
    }

    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.onerror = () => {
      setIsListening(false)
    }
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript ?? ''
      setValue(transcript)
      setIsListening(false)
    }
    setIsListening(true)
    recognition.start()
  }

  return (
    <LearningTemplateExerciseFrame
      className="flashcardGameTemplate"
      hint={
        <p className="flashcardGameTemplateHint">
          {ui("Use the microphone when supported, or type the answer as a fallback.")}</p>
      }
      media={
        <LearningTemplatePromptMedia
          audio={prompt.audio}
          audioAutoPlayKey={prompt.id}
          audioAutoPlayOnLoad
          audioVariant="flashcard"
          className="flashcardGamePromptMedia"
          image={prompt.image}
          imageAlt={ui("Flashcard prompt")}
          showAdvancedAudioControls
        />
      }
      questionText={prompt.question}
      sourceOwner="flashcards"
    >
      <div className="flashcardGameSpeechRow">
        <Field className="flashcardGameSpeechInputField">
          <FieldLabel htmlFor="flashcard-template-5-answer">
            {ui("Say or type the flashcard title")}</FieldLabel>
          <LmsInput
            id="flashcard-template-5-answer"
            onChange={(event) => {
              setValue(event.target.value)
            }}
            value={value}
          />
        </Field>
        <LmsButton
          disabled={!SpeechRecognition || isListening}
          leadingIcon={<Mic aria-hidden="true" className="size-4" />}
          onClick={handleSpeechInput}
          variant="outline"
        >
          {isListening ? ui("Listening...") : ui("Use mic")}
        </LmsButton>
      </div>
      {result ? (
        <LearningTemplateContinueActions
          incorrectReviewText={prompt.answerText ?? null}
          incorrectReviewTranslationText={prompt.answerTranslation ?? null}
          onContinue={onContinue}
          result={result}
        />
      ) : (
        <LmsButton
          className="flashcardGameCheckAction"
          disabled={!value.trim()}
          onClick={handleCheckAnswer}
          variant="primary"
        >
          {ui("Check answer")}</LmsButton>
      )}
    </LearningTemplateExerciseFrame>
  )
}
